import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sidebar } from '../../components/Sidebar';
import {
  Sparkles, Briefcase, Inbox, CheckCircle2, Clock, Building2,
  ChevronRight, PlusCircle,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_BG = 'https://static.prod-images.emergentagent.com/jobs/9de54d98-d977-4d52-98c6-87def6f7dbb6/images/270719a556826637cdb21fff7e6a54d54775e71246a523c63143781c80b4f5f3.png';

const statusMeta = {
  submitted:         { label: 'Under Review', color: '#FFB800', icon: Clock },
  under_review:      { label: 'Under Review', color: '#FFB800', icon: Clock },
  assigned:          { label: 'Assigned',     color: '#0055FF', icon: CheckCircle2 },
  in_progress:       { label: 'In Progress',  color: '#0055FF', icon: Briefcase },
  content_submitted: { label: 'Content In',   color: '#9B6EFF', icon: Clock },
  completed:         { label: 'Completed',    color: '#00C972', icon: CheckCircle2 },
  cancelled:         { label: 'Cancelled',    color: '#FF3B30', icon: Inbox },
};

export const AgencyDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API_URL}/api/agency/dashboard`, { withCredentials: true });
        setData(d);
      } catch (_e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const s = data?.stats || {};
  const clients = data?.clients || [];

  const stats = [
    { label: 'Total Clients',         value: s.total_clients ?? 0,        accent: '#FFFFFF' },
    { label: 'Active Campaigns',      value: s.active_campaigns ?? 0,     accent: '#00C972' },
    { label: 'Creators in Pipeline',  value: s.creators_in_pipeline ?? 0, accent: '#0055FF' },
    { label: 'Pending Approvals',     value: s.pending_approvals ?? 0,    accent: '#FF451A' },
  ];

  return (
    <div className="flex min-h-screen bg-white dark:bg-[#0A0B0D]">
      <Sidebar />
      <div className="flex-1 relative overflow-x-hidden" data-testid="agency-dashboard-page">
        {/* Hero */}
        <div className="relative">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }}></div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/70 to-[#050505]"></div>

          <div className="relative z-10 px-10 pt-12 pb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF451A] mb-4">
                Agency control room
              </div>
              <h1 className="font-display text-5xl md:text-6xl font-medium text-[#0F0F0F] dark:text-white leading-[1.05] tracking-tight">
                Good morning, <span className="italic text-[#FF451A]">{user?.name?.split(' ')[0] || 'Agency'}</span>.
              </h1>
              <p className="font-body text-[#6B6B6B] dark:text-zinc-400 mt-3 max-w-xl text-lg">
                {user?.company_name || 'Your agency'} — running influencer campaigns across every one of your client brands.
              </p>
              <div className="mt-7 flex gap-3 flex-wrap">
                <Link
                  to="/brand/discover?intent=agency-brief"
                  className="btn-tactile btn-tactile-primary px-7 py-3.5 rounded-md flex items-center gap-2"
                  data-testid="agency-submit-brief"
                >
                  <PlusCircle size={16} /> Submit Brief for a Client
                </Link>
                <Link
                  to="/brand/discover"
                  className="btn-tactile btn-tactile-secondary px-7 py-3.5 rounded-md flex items-center gap-2"
                  data-testid="agency-discover-creators"
                >
                  <Sparkles size={16} /> Discover Creators
                </Link>
              </div>
            </motion.div>

            {/* Glass stats */}
            <motion.div
              className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10"
              initial="hidden" animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
              data-testid="agency-stats-row"
            >
              {stats.map((stat) => (
                <motion.div
                  key={stat.label}
                  variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="glass rounded-2xl p-6 group"
                  data-testid={`agency-stat-${stat.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500 mb-2">{stat.label}</div>
                  <div className="font-mono text-3xl font-light text-[#0F0F0F] dark:text-white group-hover:text-[#FF451A] transition-colors">
                    {stat.value}
                  </div>
                  <div className="mt-3 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Body — clients grouped */}
        <div className="px-10 pb-12 space-y-8">
          <div className="card-luxe rounded-2xl p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] dark:text-zinc-500 mb-1">Client roster</div>
                <h3 className="font-display text-2xl text-[#0F0F0F] dark:text-white">Client campaigns</h3>
              </div>
              <Link to="/brand/campaigns" className="font-mono text-xs uppercase tracking-[0.2em] text-[#FF451A] hover:text-white transition-colors">
                View all →
              </Link>
            </div>

            {loading ? (
              <div className="py-10 text-center text-[#6B6B6B] dark:text-zinc-500">Loading…</div>
            ) : clients.length === 0 ? (
              <div className="py-12 text-center" data-testid="agency-no-clients">
                <Building2 className="mx-auto text-zinc-700 mb-3" size={40} />
                <h4 className="font-display text-xl text-[#0F0F0F] dark:text-white mb-1">No clients assigned yet</h4>
                <p className="text-sm text-[#6B6B6B] dark:text-zinc-500 mb-5 max-w-md mx-auto">
                  Ask your administrator to link brand accounts to your agency. Once linked, all client campaigns will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-6" data-testid="agency-clients-list">
                {clients.map((client) => (
                  <ClientBlock key={client.id} client={client} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ClientBlock = ({ client }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    viewport={{ once: true }}
    className="border border-black/[0.08] dark:border-white/5 rounded-xl overflow-hidden bg-white/[0.02]"
    data-testid={`agency-client-${client.id}`}
  >
    <div className="flex items-center gap-3 px-5 py-4 border-b border-black/[0.06] dark:border-white/5">
      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-white font-bold shrink-0">
        {(client.name?.[0] || 'B').toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-[#0F0F0F] dark:text-white truncate">{client.name}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B6B6B] dark:text-zinc-500 mt-0.5">
          {client.industry || 'Brand'} · {client.campaigns.length} campaign{client.campaigns.length === 1 ? '' : 's'}
        </div>
      </div>
    </div>

    {client.campaigns.length === 0 ? (
      <div className="px-5 py-6 text-sm text-[#6B6B6B] dark:text-zinc-500 italic">
        No campaigns submitted for this client yet.
      </div>
    ) : (
      <motion.div
        className="divide-y divide-black/[0.06] dark:divide-white/5"
        initial="hidden" animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.04 } } }}
      >
        {client.campaigns.map((camp) => {
          const meta = statusMeta[camp.status] || statusMeta.submitted;
          const Icon = meta.icon;
          return (
            <motion.div
              key={camp.id}
              variants={{ hidden: { opacity: 0, x: -8 }, visible: { opacity: 1, x: 0 } }}
            >
              <Link
                to={`/brand/campaigns?focus=${camp.id}`}
                className="flex items-center gap-4 px-5 py-4 hover:bg-white/[0.03] transition-colors group"
                data-testid={`agency-campaign-${camp.id}`}
              >
                <div
                  className="w-10 h-10 rounded-lg bg-black/40 border border-black/[0.08] dark:border-white/5 flex items-center justify-center shrink-0"
                  style={{ color: meta.color }}
                >
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#0F0F0F] dark:text-white truncate">{camp.campaign_name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B6B6B] dark:text-zinc-500 mt-0.5">
                    {camp.creator_count} creators · {camp.pending_approvals} pending approvals
                  </div>
                </div>
                <span
                  className="font-mono px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] font-semibold shrink-0"
                  style={{ backgroundColor: `${meta.color}15`, color: meta.color, border: `1px solid ${meta.color}30` }}
                >
                  {meta.label}
                </span>
                <ChevronRight size={14} className="text-[#6B6B6B] dark:text-zinc-500 shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </motion.div>
          );
        })}
      </motion.div>
    )}
  </motion.div>
);

// Retained as a named export in case a route or test imports it directly.
export const AgencyClientBlock = ClientBlock;

export default AgencyDashboard;
